import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { api } from '../api.js';
import { t, geleden, getal, wanneer } from '../i18n.js';
import { St, bronstatus, kwetsbaarheid, logging, SOORT } from '../components/Status.jsx';
import Bijgewerkt from '../components/Bijgewerkt.jsx';
import VersiesOphalen from '../components/VersiesOphalen.jsx';
import Sparkline from '../components/Sparkline.jsx';

/**
 * Bronnen, zoals in het prototype: per klant een tegel per firewall of FMC, met status en wanneer
 * hij het laatst iets stuurde. Daaronder wie syslog stuurt maar nog geen bron is, en wat er
 * verwijderd is.
 *
 * Elke vijf seconden opnieuw, met de tijd van de laatste stand bovenaan: stil worden is precies wat je
 * hier wilt zien. Een bron die stil valt, stuurt niets nieuws; de status rekent met de klok, dus de
 * pagina tekent bij elke stand opnieuw (`dataUpdatedAt`), ook als er niets veranderde.
 */
const WEERGAVE_KEY = 'adts-zero-bronnen-weergave';
const VERS_MS = 5_000;
const leesWeergave = () => { try { return localStorage.getItem(WEERGAVE_KEY) === 'tegels' ? 'tegels' : 'boom'; } catch { return 'boom'; } };

export default function Bronnen({ mij, klant }) {
  /* Boom of tegels: een voorkeur van de kijker, dus in de browser en niet bij de medewerker. */
  const [weergave, zetWeergaveRuw] = useState(leesWeergave);
  const zetWeergave = (w) => { zetWeergaveRuw(w); try { localStorage.setItem(WEERGAVE_KEY, w); } catch { /* geen opslag */ } };
  const q = useQuery({ queryKey: ['bronnen', klant], queryFn: () => api.bronnen(klant), refetchInterval: VERS_MS });
  void q.dataUpdatedAt;
  const mag = mij.permissions.includes('bronnen.beheren');
  const client = useQueryClient();
  /* Een onbekende afzender wissen (negeren), in twee stappen zonder dialoog. */
  const [wisBevestig, zetWisBevestig] = useState(null);
  const wisOnbekend = async (u) => {
    await api.wisOnbekend(u.tenant.id, u.senderIp, u.host).catch(() => {});
    zetWisBevestig(null);
    await client.invalidateQueries({ queryKey: ['bronnen'] });
  };
  if (q.isPending) return <p className="muted">{t('Laden…')}</p>;
  if (q.isError) return <div className="melder rood">{t(q.error.message)}</div>;

  const { sources, unknown } = q.data.data;
  const actief = sources.filter((s) => !s.removed);
  const weg = sources.filter((s) => s.removed);
  const klanten = mij.tenants.filter((k) => klant === 'alle' || String(k.id) === String(klant));
  const bereik = klant === 'alle' ? t('Alle klanten') : klanten[0]?.name ?? '';

  return (
    <>
      <div className="page-head">
        <div>
          <h1>{t('Bronnen')}</h1>
          <p className="sub">{t('{bereik} · {n} bronnen · stil na 5 minuten zonder bericht', { bereik, n: actief.length })}</p>
        </div>
        <div className="right">
          <Bijgewerkt q={q} elke={VERS_MS} />
          <div className="setkeuze" role="group" aria-label={t('Weergave')}>
            <button type="button" aria-pressed={weergave === 'boom'} onClick={() => zetWeergave('boom')}>{t('Boom')}</button>
            <button type="button" aria-pressed={weergave === 'tegels'} onClick={() => zetWeergave('tegels')}>{t('Tegels')}</button>
          </div>
          {mag && <VersiesOphalen />}
          {mag && <Link className="btn primary" to="/bronnen/nieuw">{t('+ Bron toevoegen')}</Link>}
        </div>
      </div>

      <Verloop klant={klant} toonKlant={klant === 'alle'} />

      {weergave === 'boom' && (
        <section className="card">
          {klanten.map((k) => <Boom key={k.id} klant={k} bronnen={actief.filter((s) => s.tenant.id === k.id)} toonKlant={klant === 'alle'} />)}
        </section>
      )}

      {weergave === 'tegels' && klanten.map((k) => {
        const hier = actief.filter((s) => s.tenant.id === k.id);
        return (
          <div key={k.id}>
            {klant === 'alle' && (
              <div className="group-h"><h2>{k.name}</h2><span className="muted">{t('{n} bronnen', { n: hier.length })}</span></div>
            )}
            <div className="fw-grid">
              {hier.map((s) => <Tegel key={s.id} bron={s} />)}
              {!hier.length && <div className="empty">{t('Nog geen bronnen.')}</div>}
            </div>
          </div>
        );
      })}

      {unknown.length > 0 && (
        <>
          <div className="group-h"><h2>{t('Onbekende afzenders')}</h2>
            <span className="muted">{t('sturen syslog maar horen bij geen bron; de laatste 24 uur')}</span></div>
          <section className="card">
            <table>
              <thead><tr><th>{t('Afzender')}</th><th>{t('Naam in syslog')}</th><th>{t('Berichten')}</th><th>{t('Laatst')}</th><th /></tr></thead>
              <tbody>
                {unknown.map((u) => (
                  <tr key={`${u.tenant.id}-${u.senderIp}-${u.host}`}>
                    <td className="mono">{u.senderIp}{klant === 'alle' && <> <span className="ttag">{u.tenant.name}</span></>}</td>
                    <td className="mono">{u.host ?? <span className="muted">{t('geen')}</span>}</td>
                    <td className="mono">{getal(u.n)}</td>
                    <td className="muted">{geleden(u.lastAt)}</td>
                    <td>{mag && (
                      <span style={{ display: 'inline-flex', gap: 12, alignItems: 'center' }}>
                        <Link to={`/bronnen/nieuw?${new URLSearchParams({ klant: u.tenant.id, naam: u.host ?? '', ip: u.senderIp })}`}>
                          {t('Als bron toevoegen')}
                        </Link>
                        {wisBevestig === `${u.tenant.id}-${u.senderIp}-${u.host}` ? (
                          <span className="muted" style={{ display: 'inline-flex', gap: 8, alignItems: 'center' }}>
                            <button type="button" className="namelink" onClick={() => wisOnbekend(u)}>{t('Wissen?')}</button>
                            <button type="button" className="namelink" onClick={() => zetWisBevestig(null)}>{t('Nee')}</button>
                          </span>
                        ) : (
                          <button type="button" className="namelink" title={t('Deze afzender uit de lijst halen; hij verschijnt niet meer, ook niet als hij blijft sturen.')}
                            onClick={() => zetWisBevestig(`${u.tenant.id}-${u.senderIp}-${u.host}`)}>{t('Wissen')}</button>
                        )}
                      </span>
                    )}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <p className="noot">{t('Hun berichten worden bewaard en geteld, maar niet bewaakt. Een firewall die je hier ziet en niet kent, is het uitzoeken waard.')}</p>
          </section>
        </>
      )}

      {weg.length > 0 && (
        <>
          <div className="group-h"><h2>{t('Verwijderd')}</h2><span className="muted">{t('wordt niet meer bewaakt; het logboek blijft')}</span></div>
          <section className="card removed-list"><div className="rows">
            {weg.map((s) => (
              <Link key={s.id} className="row" to={`/bronnen/${s.id}`} style={{ gridTemplateColumns: 'minmax(0,1fr) auto' }}>
                <span className="title">{s.name}</span>
                <span className="muted">{klant === 'alle' && <span className="ttag">{s.tenant.name}</span>} {t('door {wie} · {wanneer}', { wie: s.removed.by.name || s.removed.by.number, wanneer: wanneer(s.removed.at) })}</span>
                {s.removed.reason && <span className="det" style={{ gridColumn: '1/-1' }}>{s.removed.reason}</span>}
              </Link>
            ))}
          </div></section>
        </>
      )}
    </>
  );
}

function Tegel({ bron }) {
  const st = bronstatus(bron);
  const kw = kwetsbaarheid(bron);
  const lg = logging(bron);
  return (
    <Link className={`card fw ${st.klasse}`} to={`/bronnen/${bron.id}`}>
      <span className="name">{bron.name}</span>
      <span className="sub">
        <span className="tag">{SOORT[bron.kind]}</span>
        {bron.kind !== 'fmc' && bron.kind !== 'ise' && <span className="tag">{t(bron.profile === 'intern' ? 'Intern' : 'Aan internet')}</span>}
        {bron.version && <span className="mono">{bron.version}</span>}
      </span>
      <span style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
        <span><St soort={st.soort}>{st.label}</St>{kw && <> <St soort={kw.soort}>{kw.label}</St></>}{lg && <> <St soort={lg.soort}>{lg.label}</St></>}</span>
        <span className="muted">{bron.status === 'wacht' ? '' : geleden(bron.lastMessageAt)}</span>
      </span>
      <span className="muted" style={{ fontSize: 12 }}>
        {bron.managedBy ? t('onder {fmc}', { fmc: bron.managedBy.name }) : bron.kind === 'fmc' ? t('beheert {n}', { n: bron.manages }) : ''}
        {bron.status !== 'wacht' && <> · {t('{n} berichten het laatste uur', { n: getal(bron.lastHour) })}</>}
      </span>
    </Link>
  );
}

/**
 * De bronnen als boom: klant › FMC › de firewalls die hij beheert, en apart wat zelfstandig met FDM
 * werkt. Zo zie je in één oogopslag wat er stil valt als een FMC wegvalt, en welke firewalls bij
 * welke FMC horen — dat is ook de route waarlangs straks een blokkade gaat.
 */
function Boom({ klant, bronnen, toonKlant }) {
  const fmcs = bronnen.filter((b) => b.kind === 'fmc');
  const onder = (fmc) => bronnen.filter((b) => b.managedBy?.id === fmc.id);
  /* Een FTD onder een FMC die (nog) niet in ZERO staat, kan niet: de database eist de FMC. Wat
     overblijft, is zelfstandig. */
  const los = bronnen.filter((b) => !['fmc', 'esa', 'ise'].includes(b.kind) && !b.managedBy);
  const mail = bronnen.filter((b) => b.kind === 'esa');
  const ise = bronnen.filter((b) => b.kind === 'ise');

  const takken = (
    <ul className="boom">
      {fmcs.map((f) => (
        <li key={f.id}>
          <Knoop bron={f} />
          {onder(f).length > 0 ? (
            <ul>{onder(f).map((b) => <li key={b.id}><Knoop bron={b} /></li>)}</ul>
          ) : <ul><li className="leeg">{t('Nog geen firewalls onder deze FMC.')}</li></ul>}
        </li>
      ))}
      {los.length > 0 && (
        <li>
          <span className="knoop groep">{t('Zelfstandig (FDM)')}</span>
          <ul>{los.map((b) => <li key={b.id}><Knoop bron={b} /></li>)}</ul>
        </li>
      )}
      {mail.length > 0 && (
        <li>
          <span className="knoop groep">{t('Mail (Cisco ESA)')}</span>
          <ul>{mail.map((b) => <li key={b.id}><Knoop bron={b} /></li>)}</ul>
        </li>
      )}
      {ise.length > 0 && (
        <li>
          <span className="knoop groep">{t('Netwerktoegang (Cisco ISE)')}</span>
          <ul>{ise.map((b) => <li key={b.id}><Knoop bron={b} /></li>)}</ul>
        </li>
      )}
      {!bronnen.length && <li className="leeg">{t('Nog geen bronnen.')}</li>}
    </ul>
  );

  if (!toonKlant) return takken;
  return (
    <ul className="boom wortel">
      <li>
        <span className="knoop groep"><b>{klant.name}</b> <span className="muted">{t('{n} bronnen', { n: bronnen.length })}</span></span>
        {takken}
      </li>
    </ul>
  );
}

function Knoop({ bron }) {
  const st = bronstatus(bron);
  const kw = kwetsbaarheid(bron);
  const lg = logging(bron);
  return (
    <Link className={`knoop ${st.klasse}`} to={`/bronnen/${bron.id}`}>
      <span className="naam">{bron.name}</span>
      <span className="tag">{SOORT[bron.kind]}</span>
      {bron.kind !== 'fmc' && bron.kind !== 'ise' && <span className="tag">{t(bron.profile === 'intern' ? 'Intern' : 'Aan internet')}</span>}
      {bron.version && <span className="mono muted">{bron.version}</span>}
      <span className="rechts">
        <St soort={st.soort}>{st.label}</St>
        {kw && <St soort={kw.soort}>{kw.label}</St>}
        {lg && <St soort={lg.soort}>{lg.label}</St>}
        {bron.status !== 'wacht' && <span className="muted">{geleden(bron.lastMessageAt)} · {t('{n}/uur', { n: getal(bron.lastHour) })}</span>}
      </span>
    </Link>
  );
}

/**
 * Berichten per uur, de laatste 24 uur, per bron (docs/besluiten/meer-grafieken.md): het verloop naast elkaar, zodat
 * een bron die stil valt of opeens veel meer stuurt opvalt. Een stip in de kleur van een waarschuwing: het lopende uur is leeg.
 */
function Verloop({ klant, toonKlant }) {
  const q = useQuery({ queryKey: ['grafiek', 'bronnen', klant], queryFn: () => api.grafiek('bronnen', klant), refetchInterval: 60_000 });
  if (!q.data?.data.length) return null;
  return (
    <section className="card" style={{ marginBottom: 18 }}>
      <header><h2>{t('Berichten per uur')}</h2><span className="meta">{t('de laatste 24 uur, per bron')}</span></header>
      <div>
        {q.data.data.map((b) => (
          <div key={b.id} className="bronlijn">
            <span><Link to={`/bronnen/${b.id}`}><b>{b.name}</b></Link>{toonKlant && <> <span className="ttag">{b.tenant.name}</span></>}</span>
            <Sparkline punten={b.uren} label={t('Berichten per uur van {naam}', { naam: b.name })} />
            <span className="num">{getal(b.totaal)}</span>
          </div>
        ))}
      </div>
    </section>
  );
}
